import { ChevronRight, Shield, HelpCircle, FileText, Key, LogOut, User, Globe, DollarSign } from 'lucide-react';
import { Button } from './Button';
import { useState } from 'react';
import { LogoutModal } from './LogoutModal';
import { ChangePasswordMethodScreen } from './settings/ChangePasswordMethodScreen';
import { VerificationSecurityScreen } from './settings/VerificationSecurityScreen';
import { EnterCodeScreen } from './settings/EnterCodeScreen';
import { NewPasswordScreen } from './settings/NewPasswordScreen';
import { ResetByEmailScreen } from './settings/ResetByEmailScreen';
import { LanguageScreen } from './settings/LanguageScreen';
import { CurrencyScreen } from './settings/CurrencyScreen';
import { HelpCenterScreen } from './settings/HelpCenterScreen';
import { CancellationPolicyScreen } from './settings/CancellationPolicyScreen';
import { TermsScreen } from './settings/TermsScreen';
import { PrivacyScreen } from './settings/PrivacyScreen';
import { toast } from 'sonner';
import { AccountLayout } from './shared/AccountLayout';
import { useAuth } from '../hooks/useAuth';
import { useNavigation } from '../hooks/useNavigation';

interface SettingsRowProps {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick?: () => void;
  destructive?: boolean;
}

const SettingsRow = ({ icon, title, subtitle, onClick, destructive }: SettingsRowProps) => {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-4 hover:bg-[#F7F8FA] transition-colors group"
    >
      {icon && (
        <div className={destructive ? 'text-[#DC2626]' : 'text-[#6B7280]'}>
          {icon}
        </div>
      )}
      <div className="flex-1 text-left">
        <p className={`font-medium ${destructive ? 'text-[#DC2626]' : 'text-[#111827]'}`}>
          {title}
        </p>
        {subtitle && (
          <p className="text-xs text-[#6B7280] mt-0.5">{subtitle}</p>
        )}
      </div>
      <ChevronRight className={`w-5 h-5 transition-colors ${
        destructive 
          ? 'text-[#DC2626] group-hover:text-[#B91C1C]' 
          : 'text-[#6B7280] group-hover:text-[#FF6A2A]'
      }`} />
    </button>
  );
};

// interface SettingsToggleRowProps {
//   title: string;
//   subtitle?: string;
//   checked: boolean;
//   onChange: (checked: boolean) => void;
// }

// const SettingsToggleRow = ({ title, subtitle, checked, onChange }: SettingsToggleRowProps) => {
//   return (
//     <div className="flex items-center justify-between p-4">
//       <div className="flex-1">
//         <p className="font-medium text-[#111827]">{title}</p>
//         {subtitle && (
//           <p className="text-xs text-[#6B7280] mt-0.5">{subtitle}</p>
//         )}
//       </div>
//       <button
//         onClick={() => onChange(!checked)}
//         className={`relative w-12 h-7 rounded-full transition-colors ${
//           checked ? 'bg-[#FF6A2A]' : 'bg-[#E5E7EB]'
//         }`}
//       >
//         <span
//           className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
//             checked ? 'translate-x-5' : 'translate-x-0'
//           }`}
//         />
//       </button>
//     </div>
//   );
// };

// interface SegmentedControlProps {
//   options: string[];
//   selected: string;
//   onChange: (value: string) => void;
// }

// const SegmentedControl = ({ options, selected, onChange }: SegmentedControlProps) => {
//   return (
//     <div className="flex gap-2 p-4">
//       {options.map((option) => (
//         <button
//           key={option}
//           onClick={() => onChange(option)}
//           className={`flex-1 py-2.5 px-4 rounded-[10px] text-sm font-semibold transition-all ${
//             selected === option
//               ? 'bg-[#FF6A2A] text-white shadow-sm'
//               : 'bg-[#F7F8FA] text-[#6B7280] hover:text-[#111827]'
//           }`}
//         >
//           {option}
//         </button>
//       ))}
//     </div>
//   );
// };

interface SectionCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const SectionCard = ({ title, subtitle, children }: SectionCardProps) => {
  return (
    <div className="bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm overflow-hidden">
      <div className="p-4 border-b border-[#E5E7EB]">
        <h3 className="font-bold text-[#111827]">{title}</h3>
        {subtitle && (
          <p className="text-xs text-[#6B7280] mt-1">{subtitle}</p>
        )}
      </div>
      <div className="divide-y divide-[#E5E7EB]">
        {children}
      </div>
    </div>
  );
};

export const SettingsScreen = () => {
  // Usar hooks en lugar de props
  const { isLoggedIn, logout } = useAuth();
  const { 
    goToHome, 
    // goToMyTrips, 
    goToAccount,
    goToProfile,
    goToMyTickets,
    goToLogin,
    goBack
  } = useNavigation();

  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [language, setLanguage] = useState('Español');
  const [currency, setCurrency] = useState('MXN');
  
  // Navigation state
  type Screen = 
    | 'main'
    | 'changePasswordMethod'
    | 'verificationSecurity'
    | 'enterCode'
    | 'newPassword'
    | 'resetByEmail'
    | 'language'
    | 'currency'
    | 'helpCenter'
    | 'cancellationPolicy'
    | 'terms'
    | 'privacy';
  
  const [currentScreen, setCurrentScreen] = useState<Screen>('main');
  const [verificationMethod, setVerificationMethod] = useState<'phone' | 'email'>('phone');

  const handleLogout = () => {
    setLogoutModalOpen(true);
  };

  const handleConfirmLogout = () => {
    if (logout) {
      logout();
    }
    setLogoutModalOpen(false);
  };

  const handleCancelLogout = () => {
    setLogoutModalOpen(false);
  };

  const handlePasswordSuccess = () => {
    toast.success('Contraseña actualizada correctamente');
    setCurrentScreen('main');
  };

  const handleLanguageSave = (newLanguage: string) => {
    setLanguage(newLanguage);
    toast.success('Idioma actualizado');
  };

  const handleCurrencySave = (newCurrency: string) => {
    setCurrency(newCurrency);
    toast.success('Moneda actualizada');
  };

  const getContactInfo = () => {
    return verificationMethod === 'phone' 
      ? '+52 *** *** 5678' 
      : 'a***@example.com';
  };

  // Render subscreen
  if (currentScreen === 'changePasswordMethod') {
    return (
      <ChangePasswordMethodScreen
        onBack={() => setCurrentScreen('main')}
        onSelectInApp={() => setCurrentScreen('verificationSecurity')}
        onSelectEmail={() => setCurrentScreen('resetByEmail')}
      />
    );
  }

  if (currentScreen === 'verificationSecurity') {
    return (
      <VerificationSecurityScreen
        onBack={() => setCurrentScreen('changePasswordMethod')}
        onContinue={(method) => {
          setVerificationMethod(method);
          setCurrentScreen('enterCode');
        }}
      />
    );
  }

  if (currentScreen === 'enterCode') {
    return (
      <EnterCodeScreen
        onBack={() => setCurrentScreen('verificationSecurity')}
        onContinue={() => setCurrentScreen('newPassword')}
        method={verificationMethod}
        contact={getContactInfo()}
      />
    );
  }

  if (currentScreen === 'newPassword') {
    return (
      <NewPasswordScreen
        onBack={() => setCurrentScreen('enterCode')}
        onSuccess={handlePasswordSuccess}
      />
    );
  }

  if (currentScreen === 'resetByEmail') {
    return (
      <ResetByEmailScreen
        onBack={() => setCurrentScreen('changePasswordMethod')}
      />
    );
  }

  if (currentScreen === 'language') {
    return (
      <LanguageScreen
        onBack={() => setCurrentScreen('main')}
        onSave={handleLanguageSave}
        currentLanguage={language}
      />
    );
  }

  if (currentScreen === 'currency') {
    return (
      <CurrencyScreen
        onBack={() => setCurrentScreen('main')}
        onSave={handleCurrencySave}
        currentCurrency={currency}
      />
    );
  }

  if (currentScreen === 'helpCenter') {
    return (
      <HelpCenterScreen
        onBack={() => setCurrentScreen('main')}
      />
    );
  }

  if (currentScreen === 'cancellationPolicy') {
    return (
      <CancellationPolicyScreen
        onBack={() => setCurrentScreen('main')}
      />
    );
  }

  if (currentScreen === 'terms') {
    return (
      <TermsScreen
        onBack={() => setCurrentScreen('main')}
      />
    );
  }

  if (currentScreen === 'privacy') {
    return (
      <PrivacyScreen
        onBack={() => setCurrentScreen('main')}
      />
    );
  }

  return (
    <AccountLayout
      title="Configuración"
      subtitle="Personaliza tu experiencia y gestiona tu cuenta"
      activePage="account"
      onBack={goBack}
      onHomeClick={goToHome}
      // onMyTripsClick={goToMyTrips}
      onAccountClick={goToAccount}
      onProfileClick={goToProfile}
      onMyTicketsClick={goToMyTickets}
      onSettingsClick={() => {}} // Ya estamos en configuración
      onLogoutClick={handleLogout}
      isLoggedIn={isLoggedIn}
      onLoginClick={goToLogin}
    >
      <div className="space-y-4">
        {/* Account Section - Show only when logged out */}
        {!isLoggedIn && goToLogin && (
          <div className="bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-[#FFF4ED] rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-[#FF6A2A]" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-[#111827]">Cuenta</h3>
                <p className="text-xs text-[#6B7280] mt-0.5">Inicia sesión para gestionar tu seguridad</p>
              </div>
            </div>
            <Button
              onClick={goToLogin}
              variant="primary"
              className="w-full"
            >
              Iniciar sesión
            </Button>
          </div>
        )}

        {/* Security Section - Show only when logged in */}
        {isLoggedIn && (
          <SectionCard title="Seguridad" subtitle="Protege tu cuenta y datos personales">
            <SettingsRow
              icon={<Key className="w-5 h-5" />}
              title="Cambiar contraseña"
              subtitle="Última actualización: hace 3 meses"
              onClick={() => setCurrentScreen('changePasswordMethod')}
            />
          </SectionCard>
        )}

        {/* Preferences Section - Always visible */}
        <SectionCard title="Preferencias" subtitle="Personaliza tu experiencia de viaje">
          <SettingsRow
            icon={<Globe className="w-5 h-5" />}
            title="Idioma"
            subtitle={language}
            onClick={() => setCurrentScreen('language')}
          />
          <SettingsRow
            icon={<DollarSign className="w-5 h-5" />}
            title="Moneda"
            subtitle={currency}
            onClick={() => setCurrentScreen('currency')}
          />
        </SectionCard>

        {/* Support Section */}
        <SectionCard title="Soporte" subtitle="Obtén ayuda cuando la necesites">
          <SettingsRow
            icon={<HelpCircle className="w-5 h-5" />}
            title="Centro de ayuda"
            subtitle="Preguntas frecuentes y contacto"
            onClick={() => setCurrentScreen('helpCenter')}
          />
          <SettingsRow
            icon={<FileText className="w-5 h-5" />}
            title="Políticas de cancelación"
            subtitle="Conoce nuestras políticas"
            onClick={() => setCurrentScreen('cancellationPolicy')}
          />
        </SectionCard>

        {/* Legal Section */}
        <SectionCard title="Legal" subtitle="Términos y políticas de uso">
          <SettingsRow
            icon={<FileText className="w-5 h-5" />}
            title="Términos y condiciones"
            subtitle="Lee nuestros términos de servicio"
            onClick={() => setCurrentScreen('terms')}
          />
          <SettingsRow
            icon={<Shield className="w-5 h-5" />}
            title="Aviso de privacidad"
            subtitle="Conoce cómo protegemos tus datos"
            onClick={() => setCurrentScreen('privacy')}
          />
        </SectionCard>

        {/* Logout Button - Show only when logged in */}
        {isLoggedIn && (
          <div className="pt-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-3.5 border-2 border-[#DC2626] text-[#DC2626] font-semibold rounded-[12px] hover:bg-[#FEF2F2] transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Cerrar sesión
            </button>
          </div>
        )}

        {/* Version */}
        <div className="text-center pt-2">
          <p className="text-xs text-[#6B7280]">Versión 1.0.0</p>
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={isLogoutModalOpen}
        onConfirm={handleConfirmLogout}
        onClose={handleCancelLogout}
      />
    </AccountLayout>
  );
};

export default SettingsScreen;